import React from 'react';
import { motion } from 'framer-motion';
import { Shuffle, Link as LinkIcon } from 'lucide-react';
import { Badge } from '../../../../components/Badge';
import { QRCodeData, RoutingRule, RuleCondition } from '../types';

interface RoutingRulesSectionProps {
  qrCode: QRCodeData;
}

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
};

function getConditionText(condition: RuleCondition): string {
  if (condition.type === 'time') {
    const days = condition.days?.join(', ') || 'No days';
    return `Time (${condition.start_time || 'N/A'}-${condition.end_time || 'N/A'}, ${days})`;
  } else if (condition.type === 'geographic') {
    const countries = condition.countries || [];
    const regions = condition.regions || [];
    const cities = condition.cities || [];
    const location = countries.length > 0
      ? countries.join(', ')
      : regions.length > 0
      ? regions.join(', ')
      : cities.length > 0
      ? cities.join(', ')
      : 'Any location';
    return `Location (${location})`;
  } else if (condition.type === 'device') {
    const deviceTypes = condition.device_types || [];
    const operatingSystems = condition.operating_systems || [];
    const browsers = condition.browsers || [];
    const devices = deviceTypes.length > 0
      ? deviceTypes.join(', ')
      : operatingSystems.length > 0
      ? operatingSystems.join(', ')
      : browsers.length > 0
      ? browsers.join(', ')
      : 'Any device';
    return `Device (${devices})`;
  }
  return 'Unknown condition';
}

export function RoutingRulesSection({ qrCode }: RoutingRulesSectionProps) {
  if (!qrCode.routing_rules?.rules?.length) {
    return null;
  }

  return (
    <motion.div
      variants={itemVariants}
      initial="hidden"
      animate="visible"
      className="bg-white rounded-xl p-4 border border-[--clinqr-border]"
    >
      <h3 className="text-sm font-semibold text-[--clinqr-text-primary] mb-3 flex items-center gap-2">
        <Shuffle className="w-4 h-4 text-[--clinqr-primary]" />
        Routing Rules
        <span className="text-xs font-normal text-[--clinqr-text-secondary]">
          ({qrCode.rules_count} rule{qrCode.rules_count !== 1 ? 's' : ''})
        </span>
      </h3>
      <div className="space-y-2">
        {qrCode.routing_rules.rules.map((rule: RoutingRule, index: number) => (
          <div key={rule.id || index} className="p-3 bg-[--clinqr-bg-subtle] rounded-lg border border-[--clinqr-border]">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${
                  index === 0 ? 'bg-[--clinqr-primary] text-white' : 'bg-[--clinqr-bg-subtle] text-[--clinqr-text-secondary]'
                }`}>
                  {index === 0 ? 'IF' : 'ELSE IF'}
                </span>
                <span className="text-sm font-medium text-[--clinqr-text-primary]">{rule.name}</span>
              </div>
              <Badge variant={rule.enabled ? 'active' : 'inactive'} size="sm">
                {rule.enabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
            <div className="text-xs text-[--clinqr-text-secondary] space-y-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-medium">Conditions:</span>
                {(rule.conditions?.items?.length ?? 0) > 0 ? (
                  (rule.conditions?.items ?? []).map((condition, condIdx) => (
                    <span key={condIdx} className="px-1.5 py-0.5 bg-[--clinqr-primary] text-white rounded text-xs">
                      {getConditionText(condition)}
                      {condIdx < (rule.conditions?.items?.length ?? 0) - 1 && (
                        <span className="ml-1 font-semibold">{rule.conditions?.operator || 'AND'}</span>
                      )}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-[--clinqr-text-muted]">Always matches</span>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <LinkIcon className="w-3 h-3 text-[--clinqr-text-secondary]" />
                <span className="text-xs text-[--clinqr-primary]">
                  {rule.action?.type === 'redirect' ? (
                    <a href={rule.action.url} target="_blank" rel="noopener noreferrer" className="hover:underline truncate max-w-xs inline-block">
                      {rule.action.url}
                    </a>
                  ) : (
                    `A/B Test (${rule.action?.urls?.length || 0} variants)`
                  )}
                </span>
              </div>
            </div>
          </div>
        ))}

        {/* Fallback URL */}
        <div className="p-3 bg-[--clinqr-bg-subtle] rounded-lg border border-[--clinqr-border]/10">
          <div className="flex items-center gap-2 mb-1">
            <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-[--clinqr-primary] text-white">
              ELSE
            </span>
            <span className="text-sm font-medium text-[--clinqr-text-primary]">Default Fallback</span>
          </div>
          <div className="flex items-center gap-1.5">
            <LinkIcon className="w-3 h-3 text-[--clinqr-text-secondary]" />
            <a
              href={qrCode.fallback_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-[--clinqr-primary] hover:underline truncate max-w-xs"
            >
              {qrCode.fallback_url}
            </a>
          </div>
          <p className="text-xs text-[--clinqr-text-secondary] mt-1">
            When no rules match, users are redirected here.
          </p>
        </div>
      </div>
    </motion.div>
  );
}
